import {ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, Output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';

export interface V2LocaleOption {
  code: string;
  label: string;
  flag: string;
}

/**
 * Flag + language-name pill with a click-to-open dropdown, for pages whose
 * locale is independent of the viewer's account (e.g. a public shared
 * report). Replaces the native `<select>` previously used on this same
 * DI report page.
 */
@Component({
  selector: 'app-v2-locale-switcher',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './v2-locale-switcher.component.html',
  styleUrls: ['./v2-locale-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2LocaleSwitcherComponent {
  @Input() locales: V2LocaleOption[] = [];
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();

  public isOpen = signal(false);

  constructor(private elementRef: ElementRef<HTMLElement>) {
  }

  public get current(): V2LocaleOption | undefined {
    return this.locales.find((l) => l.code === this.value);
  }

  public toggle(): void {
    this.isOpen.set(!this.isOpen());
  }

  public select(code: string): void {
    this.isOpen.set(false);
    if (code !== this.value) {
      this.valueChange.emit(code);
    }
  }

  @HostListener('document:keydown.escape')
  public close(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  public closeOnOutsideClick(event: MouseEvent): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen.set(false);
    }
  }
}
